import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Col, Container, Row, Button } from "react-bootstrap";
import {
  Directions,
  emptyString,
  modeConstants,
  TableColumnStyle,
  translationConstants,
} from "../../../../constants";
import { UserActions, instructorActions } from "../../../../redux/actions";
import { useDispatch, useSelector } from "react-redux";
import { allSharedComponents } from "../../../../shared";
import CustomModal from "../../../../shared/components/CustomModal/CustomModal";
import cancelIcon from "../../../../assets/images/icon/crossIcon.svg";
import Tooltip from "@mui/material/Tooltip";

import {
  antSwitchStyle,
  changeTableSortIcons,
  copyToClipboard,
  debounce,
  getSortDirectionProperty,
} from "../../../../system";
import AdminUserForm from "./AdminUserForm";
import { styled } from "@mui/material/styles";
import Switch from "@mui/material/Switch";
import Stack from "@mui/material/Stack";

const { DataTable, DeleteModal } = allSharedComponents;

const initialGridState = {
  length: 10,
  pageNumber: 1,
  orderBy: null,
  filterQuery: null,
  search: null,
};

const AntSwitch = styled(Switch)(({ theme }) => antSwitchStyle(theme));

const AdminUsers = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const modalOpen = useSelector((state) => state.UserReducer.openModal);
  const usersData = useSelector((state) => state.UserReducer.adminUsersData);
  const isViewModalOpen = useSelector(
    (state) => state.InstructorReducer.isViewModalOpen
  );

  const sortingInProgressRef = useRef(false);
  const [rowData, setRowData] = useState("");
  const [gridState, setGridState] = useState(initialGridState);
  const [defaultPage, setDefaultPage] = useState(gridState.pageNumber);
  const [mode, setMode] = useState(modeConstants.ADD_MODE);
  const direction = useSelector((state) => {
    return state.LanguageChangeReducer.direction;
  });
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [userToDelete, setUserToDelete] = useState("");
  const [inputSearch, setInputSearch] = useState(emptyString);
  const searchInputRef = useRef(null);
  const [isUpdate, setIsUpdate] = useState(null);
  const [isUpdateStatusID, setIsUpdateStatusID] = useState();

  const getRowData = (data) => {
    dispatch(UserActions.getAdminUserById(data.id));
    setIsShow(true);
  };
  const removeRowData = (data) => {
    dispatch(UserActions.deleteAdminUserById(data.id));
    dispatch(instructorActions.toggleViewModal(false));
  };

  const [isShow, setIsShow] = useState(false);
  const onSetShow = () => {
    setIsShow((prev) => !prev);
  };

  const onDelete = (heading, row) => {
    setTitle(heading);
    setMessage(
      `${t(translationConstants.ARE_YOU_SURE)} ${row?.englishFirstName} ${t(
        translationConstants.FROM_ADMIN_LIST
      )}`
    );
    setUserToDelete(row);
    dispatch(instructorActions.toggleViewModal(!isViewModalOpen));
  };

  useEffect(() => {
    if (isUpdate !== null) {
      dispatch(
        UserActions.updateAdminUserStatus(
          {
            userId: isUpdateStatusID,
            comments: null,
          },
          UserActions.getAdminUsers(gridState)
        )
      );
    }
  }, [isUpdate]);

  const getcolumns = () => [
    {
      id: "id",
      accessor: "id",
      name: t(translationConstants.RECORD_ID),
      cell: (row) => (
        <Tooltip
          title={<div>{t(translationConstants.COPY_TO_CLIPBOARD)}</div>}
          placement="top"
          arrow
        >
          <div
            className="truncate-text cursor-pointer"
            onClick={() => copyToClipboard(row.id)}
          >
            {row.id}
          </div>
        </Tooltip>
      ),
      reorder: true,
      sortable: true,
      sortDirection: null,
      minWidth: TableColumnStyle.COLUMN_WIDTH_120,
      maxWidth: TableColumnStyle.COLUMN_WIDTH_120,
    },
    {
      id: "englishFirstName",
      name: t(translationConstants.name),
      selector: (row) => `${row.englishFirstName}`,
      reorder: true,
      sortable: true,
      sortDirection: null,
    },
    {
      id: "arabicFirstName",
      name: t(translationConstants.nameArabic),
      selector: (row) => `${row.arabicFirstName ? row.arabicFirstName : ""}`,
      reorder: true,
      sortable: true,
      sortDirection: null,
      cell: (row) => {
        return (
          <p className="text-center w-100-percent">
            {row.arabicFirstName ? row.arabicFirstName : ""}
          </p>
        );
      },
    },
    {
      id: "email",
      name: t(translationConstants.email),
      selector: (row) => row.email,
      reorder: true,
      sortable: true,
      sortDirection: null,
    },
    {
      id: "isActive",
      name: t(translationConstants.STATUS),
      cell: (row) => {
        let isActivate = row.isActive;
        return (
          <>
            <Stack direction="row" spacing={1} alignItems="center">
              <AntSwitch
                checked={isActivate}
                onClick={() => {
                  isActivate = !isActivate;
                  setIsUpdate((prev) => !prev);
                  setIsUpdateStatusID(row.id);
                }}
                inputProps={{ "aria-label": "ant design" }}
              />
            </Stack>
          </>
        );
      },
      reorder: true,
      sortable: true,
      sortDirection: null,
    },
    {
      id: "actions",
      name: t(translationConstants.Actions),
      cell: (row) => (
        <>
          <svg
            onClick={() => {
              getRowData(row);
              setMode(modeConstants.VIEW_MODE);
            }}
            width="20"
            height="20"
            viewBox={direction === Directions.rtl ? "0 0 20 16" : "0 0 20 16"}
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M16.1685 8.99922C16.1685 8.99922 14.0341 13.268 9.05391 13.268C4.07371 13.268 1.93933 8.99922 1.93933 8.99922C1.93933 8.99922 4.07371 4.73047 9.05391 4.73047C14.0341 4.73047 16.1685 8.99922 16.1685 8.99922Z"
              stroke="#588930"
              strokeWidth="1.42292"
              strokeLinecap="round"
            />
            <circle
              cx="9.05381"
              cy="8.99766"
              r="2.13438"
              stroke="#588930"
              strokeWidth="1.42292"
              strokeLinecap="round"
            />
          </svg>
          <svg
            onClick={() => {
              setMode(modeConstants.EDIT_MODE);
              getRowData(row);
            }}
            width="20"
            height="20"
            viewBox={direction === Directions.rtl ? "22 2 20 11" : "20 0 20 16"}
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M36.4449 13.2687V9.53359H35.3778V13.2687H26.8403V4.73125H30.5754V3.66406H26.8403C26.2509 3.66406 25.7731 4.14186 25.7731 4.73125V13.2687C25.7731 13.8581 26.2509 14.3359 26.8403 14.3359H35.3778C35.9671 14.3359 36.4449 13.8581 36.4449 13.2687ZM34.9392 4.05008C34.6946 3.80355 34.3597 3.66406 34.0103 3.66406C33.6614 3.66406 33.3268 3.80324 33.0808 4.05072L28.6153 8.51625C28.2162 8.86785 27.9546 9.39104 27.9092 9.95369L27.9074 11.6692V12.2028H30.1136C30.7189 12.1613 31.2472 11.8971 31.6199 11.4672L36.0602 7.02882C36.3066 6.78247 36.445 6.44835 36.445 6.09996C36.445 5.75157 36.3066 5.41745 36.0602 5.1711L34.9392 4.05008ZM30.076 11.1359C30.3611 11.1157 30.6264 10.9831 30.8395 10.7393L34.075 7.50381L32.6054 6.03405L29.3461 9.29222C29.1299 9.48342 28.9962 9.75092 28.9747 9.99562V11.1349L30.076 11.1359ZM33.3601 5.27956L34.8296 6.7492L35.3057 6.27316C35.3519 6.22695 35.3778 6.16427 35.3778 6.09892C35.3778 6.03357 35.3519 5.97089 35.3057 5.92468L34.1832 4.80217C34.1375 4.75611 34.0753 4.73021 34.0104 4.73021C33.9455 4.73021 33.8833 4.75611 33.8376 4.80217L33.3601 5.27956Z"
              fill="#2C7465"
            />
          </svg>
          <svg
            onClick={() => {
              onDelete(translationConstants.DELETE_A_USER, row);
            }}
            width="20"
            height="20"
            viewBox={direction === Directions.rtl ? "40 0 20 16" : "40 0 20 16"}
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ overflow: "hidden" }}
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M48.7769 2.47656H52.3342C52.9891 2.47656 53.5199 3.00745 53.5199 3.66233V4.25521H55.2987C55.9536 4.25521 56.4845 4.78609 56.4845 5.44097V6.62674C56.4845 7.28162 55.9536 7.8125 55.2987 7.8125H55.2512L54.7059 14.3344C54.7059 14.9893 54.175 15.5201 53.5201 15.5201H47.5913C46.9364 15.5201 46.4055 14.9893 46.4076 14.3836L45.86 7.8125H45.8126C45.1577 7.8125 44.6268 7.28162 44.6268 6.62674V5.44097C44.6268 4.78609 45.1577 4.25521 45.8126 4.25521H47.5911V3.66233C47.5911 3.00745 48.122 2.47656 48.7769 2.47656ZM47.5911 5.44097V5.44092H45.8126V6.62668H55.2987V5.44092H53.5199V5.44097H47.5911ZM47.5913 14.3344L47.0497 7.81268H54.0615L53.5222 14.2851L53.5201 14.3344H47.5913ZM52.3345 3.66243V4.25532H48.7772V3.66243H52.3345Z"
              fill="#FF0000"
            />
          </svg>
        </>
      ),
    },
  ];
  const [columns, setColumns] = useState(getcolumns());

  const onSort = (sortedColumn) => {
    sortingInProgressRef.current = true;
    setColumns(changeTableSortIcons(columns, sortedColumn));
    const columnDirection = getSortDirectionProperty(sortedColumn);
    setDefaultPage(gridState.pageNumber);
    setGridState((prevState) => ({
      ...prevState,
      orderBy:
        columnDirection !== null
          ? `${sortedColumn.id} ${columnDirection}`
          : null,
    }));
  };

  const onChangePage = (pageNumber) => {
    if (!sortingInProgressRef.current) {
      setGridState((prevState) => ({
        ...prevState,
        pageNumber: pageNumber,
      }));
    }
  };

  const onChangeRowsPerPage = (newPerPage, pageNumber) => {
    setGridState((prevState) => ({
      ...prevState,
      length: newPerPage,
      pageNumber: pageNumber,
    }));
  };

  useEffect(() => {
    if (usersData.pageData !== null) {
      setRowData(usersData.pageData);
    }
    // eslint-disable-next-line
  }, [usersData]);

  useEffect(() => {
    setColumns(getcolumns());
    // eslint-disable-next-line
  }, [i18n.dir()]);

  useEffect(() => {
    dispatch(UserActions.getAdminUsers(gridState));
    sortingInProgressRef.current = false;
    // eslint-disable-next-line
  }, [modalOpen, gridState]);

  useEffect(() => {
    setGridState((prevState) => ({
      ...prevState,
      search: inputSearch,
    }));
    // eslint-disable-next-line
  }, [inputSearch]);

  const handleSearch = debounce((value) => {
    setInputSearch(value);
  }, process.env.REACT_APP_SEARCH_DEBOUNCE_INTERVAL);

  const handleResetSearch = () => {
    setInputSearch("");
    setGridState((prevState) => ({
      ...prevState,
      search: "",
    }));
    searchInputRef.current.value = "";
  };

  return (
    <>
      <div className="UserDiv">
        <p className="UserTabHeading">{t(translationConstants.ADMIN_USERS)}</p>

        <div className="tableContainer">
          <Container className="m-0 p-0 mw-100">
            <Row>
              <Col
                md="3"
                xs="12"
                className="d-flex justify-content-sm-start justify-content-center align-items-center"
              >
                <div className="searchInputDiv gap-2 d-flex justify-content-between align-items-center w-100">
                  <div>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="17"
                      height="16"
                      viewBox="0 0 17 16"
                      fill="none"
                    >
                      <g clipPath="url(#clip0_610_3748)">
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M12.4015 13.3185L16.7053 17.6223C16.8565 17.7737 16.9415 17.979 16.9414 18.193C16.9413 18.407 16.8562 18.6122 16.7049 18.7635C16.5535 18.9147 16.3482 18.9997 16.1342 18.9996C15.9202 18.9995 15.715 18.9144 15.5637 18.7631L11.2599 14.4593C9.97335 15.4558 8.35548 15.9247 6.73543 15.7707C5.11539 15.6167 3.61486 14.8513 2.53911 13.6302C1.46336 12.4091 0.893196 10.8241 0.944603 9.19754C0.99601 7.571 1.66513 6.02514 2.81584 4.87443C3.96655 3.72372 5.51241 3.0546 7.13895 3.0032C8.76549 2.95179 10.3505 3.52196 11.5716 4.59771C12.7927 5.67346 13.5581 7.17398 13.7121 8.79402C13.8661 10.4141 13.3972 12.0319 12.4007 13.3185H12.4015ZM7.3417 14.1993C8.61468 14.1993 9.83553 13.6936 10.7357 12.7934C11.6358 11.8933 12.1415 10.6725 12.1415 9.3995C12.1415 8.12652 11.6358 6.90567 10.7357 6.00554C9.83553 5.10541 8.61468 4.59972 7.3417 4.59972C6.06872 4.59972 4.84788 5.10541 3.94775 6.00554C3.04762 6.90567 2.54193 8.12652 2.54193 9.3995C2.54193 10.6725 3.04762 11.8933 3.94775 12.7934C4.84788 13.6936 6.06872 14.1993 7.3417 14.1993Z"
                          fill="#575756"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_610_3748">
                          <rect
                            width="15.9645"
                            height="15.9998"
                            fill="white"
                            transform="translate(0.941406)"
                          />
                        </clipPath>
                      </defs>
                    </svg>
                  </div>
                  <input
                    className="searchInput w-100"
                    type="text"
                    placeholder={t(translationConstants.Search)}
                    onChange={(e) => handleSearch(e.target.value)}
                    ref={searchInputRef}
                  />

                  {inputSearch && (
                    <div
                      onClick={handleResetSearch}
                      className="cross cursor-pointer"
                    >
                      <img width={16} src={cancelIcon} alt="cancel icon" />
                    </div>
                  )}
                </div>
              </Col>
              <Col className="d-flex justify-content-center justify-content-sm-end align-items-center py-3">
                <Button
                  className="Registeration-button m-0 "
                  type="submit"
                  onClick={() => {
                    setMode(modeConstants.ADD_MODE);
                    onSetShow();
                  }}
                >
                  <svg
                    width="13"
                    height="14"
                    viewBox="0 0 13 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M5.6239 7.87686L0.305033 7.87686L0.305033 6.12465L5.6239 6.12465L5.6239 0.805788H7.3761L7.3761 6.12465L12.695 6.12465L12.695 7.87686L7.3761 7.87686L7.3761 13.1957H5.6239L5.6239 7.87686Z"
                      fill="white"
                    />
                  </svg>
                  &nbsp;
                  {t(translationConstants.ADD_ADMIN)}
                </Button>
              </Col>
            </Row>
          </Container>

          <DataTable
            columns={columns}
            items={rowData}
            onSort={onSort}
            gridState={gridState}
            paginationTotalRows={usersData?.page?.totalElements}
            onChangeRowsPerPage={onChangeRowsPerPage}
            onChangePage={onChangePage}
            paginationDefaultPage={defaultPage}
            paginationResetDefaultPage={true}
          />
          <CustomModal
            size="md"
            scrollable
            show={isShow}
            setShow={onSetShow}
            customClass="view-user-modal"
            closeOnOutsideClick={mode === modeConstants.VIEW_MODE}
          >
            <AdminUserForm
              setShow={setIsShow}
              mode={mode}
              gridState={gridState}
            />
          </CustomModal>

          <CustomModal
            scrollable={true}
            size="md"
            setShow={onDelete}
            show={isViewModalOpen}
            customClass="view-user-modal"
          >
            <DeleteModal
              title={title}
              message={message}
              dataToDelete={userToDelete}
              removeRowData={removeRowData}
              onCancel={() =>
                dispatch(instructorActions.toggleViewModal(!isViewModalOpen))
              }
            />
          </CustomModal>
        </div>
      </div>
    </>
  );
};

export default AdminUsers;
